import produce from 'immer';
import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PersistedDataFrameMetadataModalProps} from 'data-frames/modals/PersistedDataFrameMetadataModal';
import {
  DataFrameRenderOverrides,
  DataFrameTableServerPaginationOptions,
} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import useDataFramePageOffsetState from 'data-frames/table/hooks/useDataFramePageOffsetState';
import DataFrameActionButton from 'data-frames/toolbar/DataFrameActionButton';
import {PrimaryActionRenderFn} from 'data-frames/toolbar/DataFrameActionToolbar';
import {getMetadataModalOverflowActionForDataFrameTable} from 'data-frames/toolbar/toolbar-actions/getMetadataModalOverflowAction';
import {renderAddToAnalysisDropdown} from 'data-frames/toolbar/toolbar-actions/renderAddToAnalysisDropdown';
import getDataFramePaginationQueryVariables from 'data-frames/util/getDataFramePaginationQueryVariables';
import toDataFrameSortInput, {DataFrameSortInputVariable} from 'data-frames/util/toDataFrameSortInput';
import DataFrameTableWrapper, {
  DataFrameTableWrapperOverflowOption,
  RowSelectionProps,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/GetPersistedDataFrameQuery';
import usePrefetchPersistedDataFramePages from 'data-frames/wrapper/prefetchPersistedDataFramePages';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import {Override} from 'util/Types';

import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
  GetPersistedDataFrame_dataFrame_DataFrameNode as RawPersistedDataFrameGraphQL,
  GetPersistedDataFrame_dataFrame_InaccessibleInfoNode as InaccessibleInfoGraphQL,
} from './__generated/GetPersistedDataFrame';

export {GET_PERSISTED_DATA_FRAME_QUERY};

export type PersistedDataFrameGraphQL = Override<RawPersistedDataFrameGraphQL, {dataFrameJson: DataFrame}>;

interface PersistedDataFrameTableWrapperProps {
  actionOrigin: ActionOrigin;
  rowSelectionProps?: RowSelectionProps;
  renderIsExcludedColumn?: boolean;
  /** Optional extra overflow menu options.
   *
   * These will be rendered above the shared overflow menu options defined by this component.
   */
  additionalOverflowOptions?: Array<DataFrameTableWrapperOverflowOption>;
  includeOpenDatasetOption?: boolean;
  /** Additional button options to render in order before the overflow menu button.
   *
   * See `DataFrameActionToolbar`'s `renderElements`.
   */
  additionalActionButtons?: Array<PrimaryActionRenderFn>;
  clientStateId: string;
  dataFrameId: string;
  defaultTableMode?: DataFrameTableMode;
  additionalMetadataModalContent?: Pick<
    PersistedDataFrameMetadataModalProps,
    'additionalGridCells' | 'additionalContent'
  >;
  headerMenuOptions?: DataFrameHeaderMenuOptions;
  isCollapsible?: boolean;
  autoCollapseRowLimit?: number;
  serverPagination?: DataFrameTableServerPaginationOptions;
}

interface QueriedPersistedDataFrameTableWrapperProps extends Omit<
  PersistedDataFrameTableWrapperProps,
  'dataFrameId'
> {
  dataFrameNameOverride?: string;
  persistedDataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL;
}

interface PresentationalPersistedDataFrameTableWrapperProps extends Omit<
  PersistedDataFrameTableWrapperProps,
  'dataFrameId'
> {
  persistedDataFrame: PersistedDataFrameGraphQL;
  dataFrameRenderOverrides?: DataFrameRenderOverrides;
}

export function PresentationalPersistedDataFrameTableWrapper({
  persistedDataFrame,
  actionOrigin,
  additionalOverflowOptions,
  rowSelectionProps,
  includeOpenDatasetOption = false,
  additionalActionButtons = [],
  clientStateId,
  additionalMetadataModalContent = {},
  headerMenuOptions,
  renderIsExcludedColumn = false,
  defaultTableMode = DataFrameTableMode.TEXT,
  isCollapsible = false,
  dataFrameRenderOverrides = {},
  autoCollapseRowLimit,
  serverPagination,
}: PresentationalPersistedDataFrameTableWrapperProps): JSX.Element {
  const metadataModalOverflowOption = getMetadataModalOverflowActionForDataFrameTable(
    persistedDataFrame,
    additionalMetadataModalContent
  );

  const actionButtons = additionalActionButtons;
  if (persistedDataFrame.dataset != null) {
    actionButtons.push(
      renderAddToAnalysisDropdown({
        datasetId: persistedDataFrame.dataset?.id,
        dataFrameName: persistedDataFrame.name,
        dataFrameId: persistedDataFrame.id,
        actionOrigin,
      })
    );
    if (includeOpenDatasetOption) {
      actionButtons.push(() => (
        <DataFrameActionButton
          title="Open dataset"
          icon="open"
          onClick={() => {
            navigateToURL(persistedDataFrame.dataset!.url);
          }}
          key="OpenDatasetButton"
        />
      ));
    }
  }

  return (
    <DataFrameTableWrapper
      actionOrigin={actionOrigin}
      additionalOverflowOptions={[...(additionalOverflowOptions ?? []), metadataModalOverflowOption]}
      additionalElements={actionButtons}
      rowSelectionProps={rowSelectionProps}
      banner={
        <DataFrameWarningBanner
          isRandomSubset={false}
          name={persistedDataFrame.name}
          renderedColumns={persistedDataFrame.dataFrameJson.columns.length}
          // When server pagination is active, every row is reachable by paging, so nothing is truncated.
          renderedRows={
            serverPagination ? persistedDataFrame.numRows : persistedDataFrame.dataFrameJson.rows.length
          }
          totalColumns={persistedDataFrame.allColumnsJson.length}
          totalRows={persistedDataFrame.numRows}
        />
      }
      clientStateId={`${clientStateId}-${persistedDataFrame.id}`}
      dataFrame={persistedDataFrame.dataFrameJson}
      dataFrameId={persistedDataFrame.id}
      renderIsExcludedColumn={renderIsExcludedColumn}
      headerMenuOptions={headerMenuOptions}
      defaultTableMode={defaultTableMode}
      isCollapsible={isCollapsible}
      autoCollapseRowLimit={autoCollapseRowLimit}
      dataFrameRenderOverrides={dataFrameRenderOverrides}
      serverPagination={serverPagination}
    />
  );
}

export function QueriedPersistedDataFrameTableWrapper(
  props: QueriedPersistedDataFrameTableWrapperProps
): JSX.Element {
  const dataFrame = props.persistedDataFrame;
  return isInaccessible(dataFrame) ? (
    <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this table." />
  ) : (
    <PresentationalPersistedDataFrameTableWrapper
      {...props}
      persistedDataFrame={
        props.dataFrameNameOverride == null
          ? dataFrame
          : produce(dataFrame, (draftDataFrame) => {
              const name = props.dataFrameNameOverride ?? dataFrame.name;
              draftDataFrame.name = name;
              draftDataFrame.dataFrameJson.name = name;
            })
      }
    />
  );
}

function PersistedDataFrameTableWrapperWithPrefetch({
  dataFrameId,
  dataFrame,
  useServerPagination,
  rowsOffset,
  onChangeRowsOffset,
  sortInput,
  ...props
}: Omit<QueriedPersistedDataFrameTableWrapperProps, 'persistedDataFrame'> & {
  dataFrameId: string;
  dataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL;
  useServerPagination: boolean;
  rowsOffset: number;
  onChangeRowsOffset: (rowsOffset: number) => void;
  sortInput: DataFrameSortInputVariable | null;
}): JSX.Element {
  const dataFrameIsAccessible = !isInaccessible(dataFrame);
  usePrefetchPersistedDataFramePages(
    dataFrameId,
    rowsOffset,
    dataFrameIsAccessible ? dataFrame.numRows : 0,
    sortInput,
    useServerPagination && dataFrameIsAccessible
  );

  return (
    <QueriedPersistedDataFrameTableWrapper
      {...props}
      persistedDataFrame={dataFrame}
      serverPagination={
        useServerPagination && dataFrameIsAccessible
          ? {rowsOffset, onChangeRowsOffset, totalRows: dataFrame.numRows}
          : undefined
      }
    />
  );
}

export default function PersistedDataFrameTableWrapper(
  props: PersistedDataFrameTableWrapperProps & {
    dataFrameNameOverride?: string;
    rowLimit?: number;
  }
): JSX.Element {
  // Must match the `clientStateId` that `DataFrameTableWrapper` computes internally
  // (`${clientStateId}-${persistedDataFrame.id}`), since that's the key its `useSimpleTableSortState` and
  // pagination hooks actually read/write when the user interacts with the table.
  const tableClientStateId = `${props.clientStateId}-${props.dataFrameId}`;
  const {rowsOffset, onChangeRowsOffset} = useDataFramePageOffsetState(tableClientStateId);
  const sortOptions = useSimpleTableSortState(tableClientStateId);
  // Callers that pass an explicit `rowLimit` (e.g. small previews) want a single fixed-size fetch, not a
  // paginated view of the full data frame; server pagination is only used for the default, unbounded case,
  // and only when `ENABLE_DATA_FRAMES_NO_ROW_LIMIT` is enabled.
  const {useServerPagination, limit, offset, sortInput} = getDataFramePaginationQueryVariables({
    rowsOffset,
    sortInput: toDataFrameSortInput(sortOptions.sortState),
    rowLimit: props.rowLimit,
  });

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: props.dataFrameId, limit, offset, sortInput}}
    >
      {({dataFrame}) => (
        <PersistedDataFrameTableWrapperWithPrefetch
          {...props}
          dataFrameId={props.dataFrameId}
          dataFrame={dataFrame}
          useServerPagination={useServerPagination}
          rowsOffset={rowsOffset}
          onChangeRowsOffset={onChangeRowsOffset}
          sortInput={sortInput}
        />
      )}
    </BenchlingQuery>
  );
}
